import React from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import { compact } from 'lodash'
import clsx from 'clsx'
import {
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  AutocompleteTypeAndSearch,
  BaseDialog,
  FileUploadInput,
} from 'components'
import { DocumentOption } from './components'

import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import schema from './schema'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const LinkedDocumentModal = ({
  open,
  setOpen,
  onSubmit,
  disabledInputs,
  excludedIds = [],
  headerContent,
  accept = constants.validations.FILE_TYPES_DOCS,
  mimeTypes = constants.validations.DOCUMENT_FILE_MIMES_TYPES,
}) => {
  const { WRITTEN_CONTENT_KIND, ACTIVE_STATUS_ID } = constants.documents
  const { PRIVACY_POLICIES } = constants.permissions

  const classes = useStyles()
  const { permitted } = usePermissions()

  const localForm = useForm({
    validationSchema: schema({ accept, mimeTypes }),
    defaultValues: {
      documentType: 'attachment',
      attachments: [],
      privacyPolicyIds: [],
    },
  })

  const { control, watch, errors, handleSubmit } = localForm
  const documentType = watch('documentType')

  const isAttachment = documentType === 'attachment'
  const isLinkedDocument = documentType === 'linkedDocument'

  const hasDocumentPermissions =
    permitted(PRIVACY_POLICIES.CREATE) || permitted(PRIVACY_POLICIES.LIST)

  // Documentos gerados do mesmo modelo costumam ter títulos idênticos, então o rótulo do campo — que é
  // o que sobra no chip depois de selecionar — precisa carregar a identificação.
  const mountOptionLabel = (option) =>
    compact([
      option?.title || option?.name,
      option?.code,
      option?.id && `ID ${option.id}`,
    ]).join(' · ')

  const handleFormSubmit = (data) => {
    onSubmit(data.attachments ?? [], data.privacyPolicyIds ?? [])
    setOpen(false)
  }

  const handleSearchDocuments = async (inputValue) => {
    try {
      const response = await service.dponet.privacyPolicies.get({
        title: inputValue,
        contentKind: WRITTEN_CONTENT_KIND,
        perPage: 100000,
        minimal: true,
        excludedIds,
        order: 'title ASC',
        status: ACTIVE_STATUS_ID,
      })

      // `name` fica sendo o título puro: é o campo que a lista de evidências da tela usa para exibir o
      // documento. A identificação extra é composta no rótulo do autocomplete, em mountOptionLabel.
      return (response?.data?.privacyPolicies || []).map((privacyPolicy) => ({
        id: privacyPolicy.id,
        name: privacyPolicy.title,
        title: privacyPolicy.title,
        code: privacyPolicy.code,
        version: privacyPolicy.version,
        review: privacyPolicy.review,
      }))
    } catch {
      return []
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title="Anexar evidências"
      formRef="linked-document-modal-form"
      dialogSize="md"
      fullWidth
      actionButtonText="Selecionar"
      closeButtonText="Cancelar"
    >
      <FormContext {...localForm}>
        <Box
          component="form"
          id="linked-document-modal-form"
          onSubmit={(e) => {
            e.stopPropagation()
            handleSubmit(handleFormSubmit)(e)
          }}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          {headerContent && <Box>{headerContent}</Box>}
          <FormControl fullWidth>
            <Typography variant="h6" gutterBottom>
              Escolha o tipo de documento para a evidência:
            </Typography>
            <Box>
              <Controller
                name="documentType"
                control={control}
                as={
                  <RadioGroup
                    className={clsx(classes.radioGroup, {
                      [classes.error]:
                        !!errors?.privacyPolicyIds ||
                        !!errors?.documentType ||
                        !!errors?.attachments,
                    })}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gridGap={theme.spacing(2)}
                    >
                      <Tooltip
                        title={
                          hasDocumentPermissions
                            ? ''
                            : 'Você não possui permissões de documentos da plataforma'
                        }
                      >
                        <Paper
                          variant="outlined"
                          className={clsx(classes.card, {
                            [classes.cardError]:
                              !!errors?.privacyPolicyIds ||
                              !!errors?.documentType,
                          })}
                        >
                          <FormControlLabel
                            value="linkedDocument"
                            control={
                              <Radio
                                className={clsx({
                                  [classes.error]: !!errors?.documentType,
                                })}
                                disabled={!hasDocumentPermissions}
                              />
                            }
                            label="Documento da plataforma"
                            classes={{
                              root: clsx(classes.cardLabel, {
                                [classes.error]: !!errors?.documentType,
                              }),
                            }}
                          />
                          <Collapse in={isLinkedDocument}>
                            <Box px={2} pb={2}>
                              <AutocompleteTypeAndSearch
                                multiple
                                textField={{
                                  label: 'Documentos',
                                  error: !!errors?.privacyPolicyIds,
                                  helperText: errors?.privacyPolicyIds?.message,
                                }}
                                requestToLoadOptions={handleSearchDocuments}
                                getOptionLabel={mountOptionLabel}
                                renderOption={(option) => (
                                  <DocumentOption option={option} />
                                )}
                                classes={{ option: classes.documentOption }}
                                name="privacyPolicyIds"
                                disabled={disabledInputs}
                              />
                            </Box>
                          </Collapse>
                        </Paper>
                      </Tooltip>
                      <Paper
                        variant="outlined"
                        className={clsx(classes.card, {
                          [classes.cardError]:
                            !!errors?.attachments || !!errors?.documentType,
                        })}
                      >
                        <FormControlLabel
                          value="attachment"
                          control={
                            <Radio
                              className={clsx({
                                [classes.error]: !!errors?.documentType,
                              })}
                            />
                          }
                          label="Arquivo do computador"
                          classes={{
                            root: clsx(classes.cardLabel, {
                              [classes.error]: !!errors?.documentType,
                            }),
                          }}
                        />
                        <Collapse in={isAttachment}>
                          <Box px={2} pb={2}>
                            <FileUploadInput
                              controlName="attachments"
                              maxSize={
                                constants.validations.MAX_FILE_SIZE_30_MB
                              }
                              accept={accept}
                              multiple
                              controllerBar
                              hasDownloadFile
                              disabled={disabledInputs}
                            />
                          </Box>
                        </Collapse>
                      </Paper>
                    </Box>
                  </RadioGroup>
                }
              />
            </Box>
            {!!errors?.documentType && (
              <FormHelperText error={!!errors?.documentType}>
                {errors?.documentType?.message}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      </FormContext>
    </BaseDialog>
  )
}

export default LinkedDocumentModal
